import { entity } from 'jsonapi-transformers';

import { AbstractBibliographicResource } from './abstract-bibliographic-resource';

@entity({ type: 'resources' })
export class BibliographicResource extends AbstractBibliographicResource<BibliographicResource> {}

export type BibliographicResourceForActionApi = Pick<
  BibliographicResource,
  | 'authors'
  | 'dates'
  | 'editors'
  | 'edition'
  | 'format'
  | 'volume'
  | 'issue'
  | 'isbn10s'
  | 'isbn13s'
  | 'doi'
  | 'lcn'
  | 'publisher_names'
  | 'places_of_publication'
  | 'resource_type'
  | 'title'
  | 'issns'
  | 'eissns'
  | 'online_resource'
  | 'page_start'
  | 'page_end'
  | 'web_addresses'
  | 'source_uri'
  | 'location'
  | 'court'
  | 'series'
  | 'number'
  | 'identifier'
  | 'open_access_availability'
>;

/**
 * Copies bibiliographic resource attributes from a resource, creating a dumb object.
 * A BibliographicResource uses aliases for attributes, which are not correctly serialized
 * when used as an attribute (which is a non-standard action api). We must provide a dumb
 * javascript object for this purpose.
 * @param {BibliographicResource} resource The resource to use as a source
 * @returns {BibliographicResourceForActionApi} A dumb object for basic serialization in an action API
 */
export function copyBibliographicResourceAttributes(
  resource: BibliographicResource,
): BibliographicResourceForActionApi {
  const output: BibliographicResourceForActionApi = {
    isbn10s: resource.isbn10s,
    isbn13s: resource.isbn13s,
  };

  if (resource.authors) {
    output.authors = resource.authors;
  }

  if (resource.dates) {
    output.dates = resource.dates;
  }

  if (resource.editors) {
    output.editors = resource.editors;
  }

  if (resource.edition !== undefined) {
    output.edition = resource.edition;
  }

  if (resource.format !== undefined) {
    output.format = resource.format;
  }

  if (resource.volume !== undefined) {
    output.volume = resource.volume;
  }

  if (resource.issue !== undefined) {
    output.issue = resource.issue;
  }

  if (resource.doi !== undefined) {
    output.doi = resource.doi;
  }

  if (resource.lcn !== undefined) {
    output.lcn = resource.lcn;
  }

  if (resource.publisher_names) {
    output.publisher_names = resource.publisher_names;
  }

  if (resource.places_of_publication) {
    output.places_of_publication = resource.places_of_publication;
  }

  if (resource.resource_type) {
    output.resource_type = resource.resource_type;
  }

  if (resource.title) {
    output.title = resource.title;
  }

  if (resource.issns) {
    output.issns = resource.issns;
  }

  if (resource.eissns) {
    output.eissns = resource.eissns;
  }

  if (resource.online_resource) {
    output.online_resource = resource.online_resource;
  }

  if (resource.page_start !== undefined) {
    output.page_start = resource.page_start;
  }

  if (resource.page_end !== undefined) {
    output.page_end = resource.page_end;
  }

  if (resource.web_addresses) {
    output.web_addresses = resource.web_addresses;
  }

  if (resource.source_uri !== undefined) {
    output.source_uri = resource.source_uri;
  }

  if (resource.location !== undefined) {
    output.location = resource.location;
  }

  if (resource.court !== undefined) {
    output.court = resource.court;
  }

  if (resource.series !== undefined) {
    output.series = resource.series;
  }

  if (resource.number !== undefined) {
    output.number = resource.number;
  }

  if (resource.identifier !== undefined) {
    output.identifier = resource.identifier;
  }

  if (resource.open_access_availability) {
    output.open_access_availability = resource.open_access_availability;
  }

  return output;
}
